/**
 * ServiceHistoryModal — read a customer's service history, then print, download
 * or email it as a branded document.
 *
 * The modal opens on the history itself (what work was done, when, at what
 * mileage) — the email composer is a second tab, not the landing screen.
 *
 * Scope:
 *   - no vehicleId  → all of the customer's vehicles, grouped.
 *   - vehicleId set → that one vehicle (used at resale / after a specific job).
 *
 * The PDF is generated locally from ServiceHistoryPDF. Email reuses the
 * send-customer-email edge function with the PDF attached (pdf_base64).
 */
import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { X, Printer, Download, Send, Loader2, AlertCircle, CheckCircle, ClipboardList, Paperclip } from 'lucide-react';
import { pdf } from '@react-pdf/renderer';
import { supabase } from '../../../lib/supabase';
import { urlToBase64, LOGO_MAX_PX } from '../../../lib/images';
import { isValidEmail, readFunctionError } from '../../../lib/email';
import {
    ServiceHistoryPDF,
    type ServiceHistoryIdentity,
    type ServiceHistoryVehicleGroup,
    type ServiceRecord,
} from './ServiceHistoryPDF';
import { buildServiceHistoryPdfBase64 } from './buildServiceHistoryPdf';
import { formatServiceMileage, type ServiceMileageSource } from './mileage';
import { errorMessage, reportError } from '../../../lib/errors';
import toast from 'react-hot-toast';
import { DEFAULT_DOCUMENT_ACCENT } from '../../../lib/brandAccent';

interface ServiceHistoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    customerId: string;
    customerName: string;
    customerEmail?: string;
    customerPhone?: string;
    orgId: string;
    businessEmail?: string;
    /** When set, the document is scoped to a single vehicle. */
    vehicleId?: string;
}

type Status = 'idle' | 'generating' | 'sending' | 'success';

export default function ServiceHistoryModal({
    isOpen,
    onClose,
    customerId,
    customerName,
    customerEmail,
    customerPhone,
    orgId,
    businessEmail,
    vehicleId,
}: ServiceHistoryModalProps) {
    const [loading, setLoading] = useState(true);
    const [identity, setIdentity] = useState<ServiceHistoryIdentity | null>(null);
    const [logoBase64, setLogoBase64] = useState<string | undefined>(undefined);
    const [groups, setGroups] = useState<ServiceHistoryVehicleGroup[]>([]);
    // A service history documents the work done, not costs — prices off by default.
    const [showPrices, setShowPrices] = useState(false);

    // Email composer state
    const [emailTo, setEmailTo] = useState(customerEmail || '');
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [sendCopy, setSendCopy] = useState(false);

    const [status, setStatus] = useState<Status>('idle');
    const [busyAction, setBusyAction] = useState<'print' | 'download' | null>(null);
    const [error, setError] = useState<string | null>(null);
    // Reading the history is the default; emailing it is the second step.
    const [tab, setTab] = useState<'history' | 'send'>('history');

    const recordCount = useMemo(() => groups.reduce((n, g) => n + g.records.length, 0), [groups]);
    const vehicleCount = useMemo(() => groups.filter((g) => g.records.length > 0).length, [groups]);

    // Single-vehicle scope → use its reg in the copy.
    const singleVehicleReg = vehicleId && groups.length === 1 ? groups[0].vehicle?.vrm : undefined;

    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setError(null);
            setStatus('idle');
            setTab('history');

            try {
                // 1. Live org identity for the header.
                const { data: org, error: orgError } = await supabase
                    .from('organizations')
                    .select('name, branding, business_address, business_phone, business_email, vat_number, subscription_tier')
                    .eq('id', orgId)
                    .single();
                if (orgError) reportError(orgError, 'service-history-modal.organizations');

                const branding = (org as any)?.branding || {};
                const id: ServiceHistoryIdentity = {
                    name: org?.name || 'Your Workshop',
                    logo_url: branding.logo_url || undefined,
                    address: (org as any)?.business_address || undefined,
                    phone: (org as any)?.business_phone || undefined,
                    email: (org as any)?.business_email || undefined,
                    vat_number: (org as any)?.vat_number || undefined,
                    accent: branding.invoice_accent_color || branding.primary_color || DEFAULT_DOCUMENT_ACCENT,
                    tier: (org as any)?.subscription_tier || 'free',
                };

                // 2. Service history records (+ vehicle), scoped if a vehicle is given.
                //    Apply all .eq() filters BEFORE .order() — .order() returns a
                //    transform builder that no longer exposes .eq().
                let query = supabase
                    .from('service_history')
                    .select('service_date, mileage, mileage_source, title, summary, line_items, total, vehicle_id, vehicles(vrm, make, model, year)')
                    .eq('customer_id', customerId);
                if (vehicleId) query = query.eq('vehicle_id', vehicleId);

                const { data: rows, error: shError } = await query.order('service_date', { ascending: false });
                if (shError) throw shError;

                // 3. Group by vehicle (records already newest-first).
                const groupsMap = new Map<string, ServiceHistoryVehicleGroup>();
                for (const r of (rows ?? []) as any[]) {
                    const key = r.vehicle_id || 'none';
                    if (!groupsMap.has(key)) {
                        groupsMap.set(key, { vehicle: r.vehicles || null, records: [] });
                    }
                    const rec: ServiceRecord = {
                        service_date: r.service_date,
                        mileage: r.mileage,
                        mileage_source: r.mileage_source,
                        title: r.title,
                        summary: r.summary,
                        line_items: Array.isArray(r.line_items) ? r.line_items : [],
                        total: r.total,
                    };
                    groupsMap.get(key)!.records.push(rec);
                }

                if (cancelled) return;
                setIdentity(id);
                setGroups(Array.from(groupsMap.values()));

                // 4. Logo → base64 so it renders in the PDF (CORS-safe).
                if (branding.logo_url) {
                    urlToBase64(branding.logo_url, { maxDimension: LOGO_MAX_PX })
                        .then((b64) => { if (!cancelled) setLogoBase64(b64 ?? undefined); })
                        .catch((e) => console.error('Logo conversion failed:', e));
                }
            } catch (err) {
                console.error('ServiceHistoryModal load failed:', err);
                if (!cancelled) setError(errorMessage(err) || 'Failed to load service history');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => { cancelled = true; };
    }, [isOpen, orgId, customerId, vehicleId]);

    // Prefill email copy once we know scope.
    useEffect(() => {
        if (!isOpen) return;
        setEmailTo(customerEmail || '');
        setSendCopy(false);
        const bizName = identity?.name || 'us';
        const forWhat = singleVehicleReg ? `your vehicle (${singleVehicleReg})` : 'your vehicle';
        setSubject(`Service history${singleVehicleReg ? ` — ${singleVehicleReg}` : ''} from ${bizName}`);
        setMessage(
            `Hi ${customerName || 'there'},\n\n` +
            `Please find attached the service history for ${forWhat}, covering the work we've carried out.\n\n` +
            `Keep this for your records — it's handy proof of a maintained vehicle.\n\n` +
            `Best regards,\n${bizName}`
        );
    }, [isOpen, identity, customerEmail, customerName, singleVehicleReg]);

    if (!isOpen) return null;

    const makeDoc = () => (
        <ServiceHistoryPDF
            identity={{ ...(identity as ServiceHistoryIdentity), logoBase64 }}
            customer={{ full_name: customerName, email: customerEmail, phone: customerPhone }}
            groups={groups}
            showPrices={showPrices}
        />
    );

    const fileName = `Service_History_${(singleVehicleReg || customerName || 'customer').replace(/[^a-z0-9]+/gi, '_')}.pdf`;

    // Email attachments route through the shared builder — the same one
    // CustomerEmailModal's "Service history" template uses — so both entry
    // points produce an identical document instead of one silently lacking it.
    const generatePdfBase64 = async (): Promise<string | null> => {
        const result = await buildServiceHistoryPdfBase64({
            orgId,
            customerId,
            vehicleId,
            customerName,
            customerEmail,
            customerPhone,
            showPrices,
        });
        return result?.pdfBase64 ?? null;
    };

    const handleDownload = async () => {
        if (!identity || recordCount === 0) return;
        setBusyAction('download');
        try {
            const blob = await pdf(makeDoc()).toBlob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error(err);
            setError('Failed to generate PDF.');
        } finally {
            setBusyAction(null);
        }
    };

    const handlePrint = async () => {
        if (!identity || recordCount === 0) return;
        setBusyAction('print');
        try {
            const printWindow = window.open('', '_blank');
            if (!printWindow) {
                toast.error('Please allow pop-ups to print.');
                return;
            }
            printWindow.document.write('<html><body style="font-family:sans-serif;text-align:center;padding-top:50px;">Preparing document…</body></html>');
            const blob = await pdf(makeDoc()).toBlob();
            printWindow.location.href = URL.createObjectURL(blob);
        } catch (err) {
            console.error(err);
            setError('Failed to prepare print document.');
        } finally {
            setBusyAction(null);
        }
    };

    const handleSend = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (recordCount === 0) { setError('There are no service records to send.'); return; }
        if (!emailTo.trim()) { setError('Recipient email is required'); return; }
        if (!isValidEmail(emailTo)) { setError('That email address looks invalid — check it includes a domain like "name@example.com".'); return; }
        if (!subject.trim()) { setError('Please add a subject.'); return; }

        try {
            setStatus('generating');
            const pdfBase64 = await generatePdfBase64();
            if (!pdfBase64) throw new Error('Could not generate the PDF attachment.');

            setStatus('sending');
            const { data, error: fnError } = await supabase.functions.invoke('send-customer-email', {
                body: {
                    org_id: orgId,
                    email: emailTo.trim(),
                    customer_name: customerName,
                    customer_id: customerId,
                    template_type: 'service_history',
                    subject: subject.trim(),
                    message: message.trim(),
                    send_copy: sendCopy,
                    business_email: businessEmail,
                    pdf_base64: pdfBase64,
                    attachment_filename: fileName,
                },
            });
            if (fnError) throw await readFunctionError(fnError, 'Failed to send email');
            if (data?.error) throw new Error(data.error);

            setStatus('success');
            setTimeout(() => { onClose(); }, 1500);
        } catch (err) {
            console.error('Service history email error:', err);
            setError(errorMessage(err) || 'Failed to send email');
            setStatus('idle');
        }
    };

    const busy = status !== 'idle' || busyAction !== null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-scrim/80 backdrop-blur-sm animate-in fade-in">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="w-full max-w-lg bg-panel border border-line rounded-xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
            >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-tint/10 shrink-0">
                    <h2 className="text-xl font-bold text-fg flex items-center gap-2">
                        <ClipboardList className="w-5 h-5 text-accent" />
                        Service History
                    </h2>
                    <button
                        onClick={onClose}
                        disabled={status === 'generating' || status === 'sending'}
                        className="p-2 rounded-full hover:bg-tint/10 transition-colors text-fg-3 hover:text-fg disabled:opacity-50"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Tabs — read the history, or send it. */}
                {!loading && recordCount > 0 && (
                    <div className="flex shrink-0 border-b border-tint/10 bg-sunken">
                        {([
                            ['history', `History (${recordCount})`],
                            ['send', 'Send to customer'],
                        ] as const).map(([key, label]) => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => setTab(key)}
                                className={`px-5 py-3 text-sm font-bold transition-colors border-b-2 -mb-px ${
                                    tab === key
                                        ? 'text-fg border-accent'
                                        : 'text-fg-4 border-transparent hover:text-fg-2'
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                )}

                <div className="p-6 space-y-4 overflow-y-auto">
                    {loading ? (
                        <div className="flex items-center justify-center py-10">
                            <Loader2 className="w-6 h-6 text-accent animate-spin" />
                        </div>
                    ) : recordCount === 0 ? (
                        <div className="text-center py-8 text-sm text-fg-3">
                            No completed services on record yet. A record is saved automatically each time an invoice is paid.
                        </div>
                    ) : tab === 'history' ? (
                        <>
                            {/* Summary + document actions */}
                            <div className="rounded-lg border border-tint/10 bg-tint/5 p-4 space-y-3">
                                <p className="text-sm text-fg-2">
                                    <span className="font-bold text-fg">{recordCount}</span> service{recordCount === 1 ? '' : 's'}
                                    {' '}across <span className="font-bold text-fg">{vehicleCount}</span> vehicle{vehicleCount === 1 ? '' : 's'}
                                    {singleVehicleReg && <> · <span className="font-mono text-accent">{singleVehicleReg}</span></>}
                                </p>

                                <label className="flex items-center gap-2 select-none cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={showPrices}
                                        onChange={(e) => setShowPrices(e.target.checked)}
                                        disabled={busy}
                                        className="rounded border-tint/10 bg-tint/5 text-accent focus:ring-accent disabled:opacity-50"
                                    />
                                    <span className="text-sm text-fg-2">Show prices on the document</span>
                                </label>

                                <div className="flex flex-wrap gap-2 pt-1">
                                    <button
                                        type="button"
                                        onClick={handlePrint}
                                        disabled={busy}
                                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-tint/5 border border-tint/10 text-sm font-medium text-fg-2 hover:bg-tint/10 transition-all disabled:opacity-50"
                                    >
                                        {busyAction === 'print' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
                                        Print
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleDownload}
                                        disabled={busy}
                                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-tint/5 border border-tint/10 text-sm font-medium text-fg-2 hover:bg-tint/10 transition-all disabled:opacity-50"
                                    >
                                        {busyAction === 'download' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                                        Download PDF
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setTab('send')}
                                        disabled={busy}
                                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-accent/10 border border-accent/30 text-sm font-medium text-accent hover:bg-accent/20 transition-all disabled:opacity-50"
                                    >
                                        <Send className="w-4 h-4" />
                                        Email it
                                    </button>
                                </div>
                            </div>

                            {/* The actual history, grouped by vehicle */}
                            {groups.map((g, gi) => (
                                <div key={g.vehicle?.vrm ?? gi} className="space-y-2">
                                    <div className="flex items-center gap-2 pt-1">
                                        <span className="font-mono text-xs font-bold bg-raised text-fg px-2 py-1 rounded border border-tint/10">
                                            {g.vehicle?.vrm || 'Unknown vehicle'}
                                        </span>
                                        <span className="text-sm text-fg-3 truncate">
                                            {[g.vehicle?.year, g.vehicle?.make, g.vehicle?.model].filter(Boolean).join(' ')}
                                        </span>
                                    </div>

                                    {g.records.map((rec, ri) => (
                                        <div
                                            key={`${gi}-${ri}`}
                                            className="rounded-lg border border-tint/10 bg-tint/5 p-4"
                                        >
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-3 text-xs text-fg-3">
                                                        <span>
                                                            {new Date(rec.service_date).toLocaleDateString('en-GB', {
                                                                day: 'numeric', month: 'short', year: 'numeric',
                                                            })}
                                                        </span>
                                                        {(() => {
                                                            const m = formatServiceMileage(rec.mileage, rec.mileage_source as ServiceMileageSource);
                                                            return m && (
                                                                <span className="font-mono">
                                                                    {m.value}
                                                                    {m.qualifier && <span className="ml-1 not-italic font-sans text-fg-4">{m.qualifier}</span>}
                                                                </span>
                                                            );
                                                        })()}
                                                    </div>
                                                    <h4 className="font-bold text-fg mt-1 break-words">{rec.title}</h4>
                                                    {rec.summary && (
                                                        <p className="text-sm text-fg-3 mt-0.5 whitespace-pre-wrap">{rec.summary}</p>
                                                    )}
                                                </div>
                                                {rec.total != null && (
                                                    <span className="shrink-0 font-mono text-sm font-bold text-fg">
                                                        £{Number(rec.total).toFixed(2)}
                                                    </span>
                                                )}
                                            </div>

                                            {(rec.line_items?.length ?? 0) > 0 && (
                                                <ul className="mt-3 pt-3 border-t border-tint/5 space-y-1">
                                                    {(rec.line_items ?? []).map((li: any, li_i: number) => (
                                                        <li key={li_i} className="text-xs text-fg-3 flex justify-between gap-3">
                                                            <span className="truncate">
                                                                {li.quantity ? `${li.quantity}× ` : ''}{li.description || 'Item'}
                                                            </span>
                                                            {li.total != null && (
                                                                <span className="font-mono text-fg-2 shrink-0">
                                                                    £{Number(li.total).toFixed(2)}
                                                                </span>
                                                            )}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </>
                    ) : (
                        <>
                            {/* Email composer */}
                            <form onSubmit={handleSend} className="space-y-4">
                                <div>
                                    <label className="block text-xs font-bold text-fg-4 uppercase mb-1">Email to</label>
                                    <input
                                        type="email"
                                        value={emailTo}
                                        onChange={(e) => setEmailTo(e.target.value)}
                                        disabled={status !== 'idle'}
                                        placeholder="customer@example.com"
                                        className="w-full bg-tint/5 border border-tint/10 rounded-lg px-4 py-2.5 text-fg focus:ring-2 focus:ring-accent outline-none disabled:opacity-50 placeholder:text-fg-4"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-fg-4 uppercase mb-1">Subject</label>
                                    <input
                                        type="text"
                                        value={subject}
                                        onChange={(e) => setSubject(e.target.value)}
                                        disabled={status !== 'idle'}
                                        className="w-full bg-tint/5 border border-tint/10 rounded-lg px-4 py-2.5 text-fg focus:ring-2 focus:ring-accent outline-none disabled:opacity-50"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-fg-4 uppercase mb-1">Message</label>
                                    <textarea
                                        rows={6}
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        disabled={status !== 'idle'}
                                        className="w-full bg-tint/5 border border-tint/10 rounded-lg px-4 py-2.5 text-fg focus:ring-2 focus:ring-accent outline-none resize-none disabled:opacity-50"
                                    />
                                </div>

                                {/* Attachment indicator */}
                                <div className="flex items-center gap-3 p-3 border border-tint/10 rounded-lg bg-tint/5">
                                    <div className="w-10 h-10 bg-danger/20 rounded flex items-center justify-center">
                                        <span className="text-danger font-bold text-[10px]">PDF</span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-fg truncate">{fileName}</p>
                                        <p className="text-xs text-fg-4 flex items-center gap-1">
                                            <Paperclip className="w-3 h-3" /> Attached automatically ({showPrices ? 'with prices' : 'no prices'})
                                        </p>
                                    </div>
                                    <CheckCircle className="w-5 h-5 text-success" />
                                </div>

                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="shSendCopy"
                                        checked={sendCopy}
                                        onChange={(e) => setSendCopy(e.target.checked)}
                                        disabled={status !== 'idle'}
                                        className="rounded border-tint/10 bg-tint/5 text-accent focus:ring-accent disabled:opacity-50"
                                    />
                                    <label htmlFor="shSendCopy" className="text-sm text-fg-3 select-none cursor-pointer">
                                        Send a copy to my email {businessEmail && `(${businessEmail})`}
                                    </label>
                                </div>

                                {error && (
                                    <div className="p-3 bg-danger/10 border border-danger/20 rounded-lg flex items-center gap-2 text-danger text-sm">
                                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                        {error}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={status !== 'idle' || busyAction !== null}
                                    className={`w-full font-bold py-2.5 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 ${status === 'success' ? 'bg-success-fill text-on-fill' : 'fd-btn-primary'}`}
                                >
                                    {status === 'idle' && (<><Send className="w-5 h-5" />Email Service History</>)}
                                    {status === 'generating' && (<><Loader2 className="w-5 h-5 animate-spin" />Generating PDF…</>)}
                                    {status === 'sending' && (<><Loader2 className="w-5 h-5 animate-spin" />Sending…</>)}
                                    {status === 'success' && (<><CheckCircle className="w-5 h-5" />Sent!</>)}
                                </button>
                            </form>
                        </>
                    )}

                    {/* Load error (pre-records) */}
                    {!loading && error && recordCount === 0 && (
                        <div className="p-3 bg-danger/10 border border-danger/20 rounded-lg flex items-center gap-2 text-danger text-sm">
                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                            {error}
                        </div>
                    )}
                </div>
            </motion.div>
        </div>
    );
}
